import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TEL_HREF, WHATSAPP_HREF, PHONE_DISPLAY } from "@/lib/ascentix";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "Leadership Development",
  "Business Development",
  "Relationship Management",
  "Digital Literacy & AI Readiness",
  "Coaching & Mentoring",
  "Facilitation & Consulting",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    organisation: "",
    role: "",
    email: "",
    phone: "",
    area_of_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({
        name: "",
        organisation: "",
        role: "",
        email: "",
        phone: "",
        area_of_interest: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try WhatsApp or call.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#f4f1ea]/40"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT info */}
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Start the Conversation</span>
            <h2
              data-testid="contact-headline"
              className="font-display mt-4 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-slate-900"
            >
              Start a conversation with{" "}
              <span className="text-orange-600">Ascentix</span>.
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-slate-600 max-w-[480px]">
              Whether you are planning a leadership programme, strengthening
              your management team, improving customer relationships or
              preparing your organisation for digital transformation — we can
              help you design a practical development solution.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={TEL_HREF}
                data-testid="contact-call-card"
                className="flex items-center gap-4 rounded-2xl bg-white border border-slate-900/5 p-5 hover:border-orange-300 transition-colors"
              >
                <div className="size-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                  <Phone className="size-6" />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-slate-500">
                    Call directly
                  </div>
                  <div className="font-display-medium text-slate-900">
                    {PHONE_DISPLAY}
                  </div>
                </div>
              </a>
              <a
                href={WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-card"
                className="flex items-center gap-4 rounded-2xl bg-white border border-slate-900/5 p-5 hover:border-orange-300 transition-colors"
              >
                <div className="size-12 rounded-xl bg-[#25D366]/10 text-[#128C7E] flex items-center justify-center shrink-0">
                  <MessageCircle className="size-6" />
                </div>
                <div>
                  <div className="text-[12px] uppercase tracking-wider text-slate-500">
                    WhatsApp Ascentix
                  </div>
                  <div className="font-display-medium text-slate-900">
                    Quick replies during business hours
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* RIGHT form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl bg-white border border-slate-900/5 p-6 md:p-8 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.18)]"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name *</Label>
                  <Input
                    id="name"
                    data-testid="contact-input-name"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organisation">Organisation</Label>
                  <Input
                    id="organisation"
                    data-testid="contact-input-organisation"
                    placeholder="Company / institution"
                    value={form.organisation}
                    onChange={update("organisation")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input
                    id="role"
                    data-testid="contact-input-role"
                    placeholder="e.g. Head of HR"
                    value={form.role}
                    onChange={update("role")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    placeholder="you@organisation.com"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone / WhatsApp</Label>
                  <Input
                    id="phone"
                    data-testid="contact-input-phone"
                    placeholder="+234..."
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="area">Area of interest</Label>
                  <Select
                    value={form.area_of_interest}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, area_of_interest: v }))
                    }
                  >
                    <SelectTrigger
                      id="area"
                      data-testid="contact-select-interest"
                    >
                      <SelectValue placeholder="Select an area" />
                    </SelectTrigger>
                    <SelectContent>
                      {interests.map((i) => (
                        <SelectItem
                          key={i}
                          value={i}
                          data-testid={`interest-option-${i
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")}`}
                        >
                          {i}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    placeholder="Tell us about your team, goals or upcoming programmes..."
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    required
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="rounded-full bg-orange-600 hover:bg-orange-700 text-white h-12 px-7 font-semibold shadow-[0_14px_30px_-12px_rgba(234,88,12,0.65)]"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Enquiry
                      <Send className="size-4 ml-2" />
                    </>
                  )}
                </Button>
                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-cta-whatsapp"
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-slate-900/10 bg-white hover:border-orange-600 hover:text-orange-600 transition-colors text-[15px] font-medium"
                >
                  <MessageCircle className="size-4" />
                  WhatsApp Ascentix
                </a>
                <a
                  href={TEL_HREF}
                  data-testid="contact-cta-call"
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-full text-slate-800 hover:text-orange-600 transition-colors text-[15px] font-medium"
                >
                  <Phone className="size-4" />
                  Call {PHONE_DISPLAY}
                </a>
              </div>
              <p className="mt-4 text-[12px] text-slate-500">
                We typically respond within one business day.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
